import React from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import Icon from '../../app/common/components/Icon';
import ICONS from '../../app/common/constants/icons';
import { COLORS, FONT_FAMILY, MQS, SPACINGS } from '../../app/common/constants/styles';
import { IRoute } from '../../routes/routes';
import SignOutLinkContainer from '../../signOut/SignOutLinkContainer';
import { TranslationHelpers } from '../../i18n/translationHelpers';
import * as webStorage from '@com.rakuten.esd/myr-web-storage';
import { PageId } from '../../config/constants/pageId.enum';
import { injectIntl, InjectedIntlProps } from 'react-intl';
import { addRatEvent } from '../../utils/ratClickEvents';

const List = styled.ul`
    list-style: none;
    padding: 0 0 50px;
    margin: 0;
`;

export const NameIcon = styled.div`
    display: flex;
    align-items: center;
    flex: 1;
    min-width: 0;
    margin-right: 8px;

    & span {
        margin-left: 15px;
    }
    .sidebar__name-icon__left {
        margin-right: 8px;
        flex-shrink: 0;
        overflow: visible;

        svg {
            overflow: visible;
        }
    }
`;

const ListItem = styled.li`
    background-color: transparent;
    width: 100%;

    &:hover,
    &:focus {
        background: #f2f2f2;
    }

    &:not(:first-child) {
        border-top: 1px solid ${COLORS.lightGrey};
    }

    &.current {
        border-radius: 4px;
        background-color: ${COLORS.lightGrey};
        ${FONT_FAMILY.multipleBoldCss};
        font-weight: 800;
    }
`;

const ListItemLink = styled(Link)`
    text-decoration: none;
    padding: ${SPACINGS.base * 2}px;
    color: #333;
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

const ExternalLink = styled.a`
    cursor: pointer;
    text-decoration: none;
    padding: ${SPACINGS.base * 2}px;
    color: #333;
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

const SignOutButton = styled.button`
    cursor: pointer;
    text-decoration: none;
    padding: ${SPACINGS.base * 2}px;
    color: #333;
    display: flex;
    align-items: center;
    justify-content: start;
    border: none;
    background: none;
    width: 100%;
`;

const BackLinkWrapper = styled.div`
    position: absolute;
    right: 0;
    width: 100%;

    padding-top: ${SPACINGS.base * 5}px;

    a {
        padding-bottom: 0;
    }

    svg,
    span {
        color: #aeaeae;
    }

    @media (max-width: ${MQS.mq3Min}) {
        padding: 0 20px 20px;
    }
`;

interface ISideBarOwnProps {
    activeRoute: IRoute;
    routes: IRoute[];
    location: Location;
}

interface ISideBarStateProps {
    returnUrl: string;
    returnUrlText: string;
    callbackUrl: string;
    returnUrlEnabled: boolean;
    targetUrl: string;
    isShowMyData: boolean;
    isShowSignOut: boolean;
    hasEKYC: boolean;
    hasSocialLink: boolean;
}

type SideBarProps = ISideBarOwnProps & ISideBarStateProps & RouteComponentProps & InjectedIntlProps;

const SideBar = ({
    activeRoute,
    returnUrl,
    returnUrlText,
    routes,
    returnUrlEnabled,
    targetUrl,
    isShowMyData,
    isShowSignOut,
    intl,
    hasEKYC,
    hasSocialLink,
}: SideBarProps) => {
    const routesMap = routes.filter(
        (route: IRoute) =>
            (route.id !== 'ekyc' || hasEKYC) && (route.id !== 'linked.accounts' || hasSocialLink),
    );

    const newTabAriaLabel = intl.formatMessage(
        TranslationHelpers.getMessageDescriptor('newTab.ariaLabel'),
    );
    return (
        <List>
            <ListItem key={PageId.overview}>
                <ListItemLink data-qa-id={`side-menu-${PageId.overview}`} to={'/'} tabIndex={0}>
                    <NameIcon>
                        <Icon
                            className={'sidebar__name-icon__left'}
                            icon={ICONS.HOME}
                            size={'24'}
                            viewBoxMax={'24'}
                            fillRule="evenodd"
                            clipRule="evenodd"
                        />
                        {TranslationHelpers.getFormattedMessage('overview.home')}
                    </NameIcon>
                    <Icon
                        icon={ICONS.ARROW_RIGHT}
                        size={'18'}
                        fillRule="evenodd"
                        clipRule="evenodd"
                    />
                </ListItemLink>
            </ListItem>
            {routesMap.map(({ id, path, name, icon }) => (
                <ListItem key={id} className={activeRoute.id === id ? 'current' : ''}>
                    <ListItemLink data-qa-id={`side-menu-${id}`} to={path} tabIndex={0}>
                        <NameIcon>
                            <Icon
                                className={'sidebar__name-icon__left'}
                                icon={icon}
                                size={'24'}
                                viewBoxMax={'24'}
                                fillRule="evenodd"
                                clipRule="evenodd"
                                stroke={activeRoute.id === id ? COLORS.dark1 : ''}
                                strokeWidth={activeRoute.id === id ? '0.5px' : '0px'}
                            />
                            {TranslationHelpers.getFormattedMessage(name.id)}
                        </NameIcon>
                        <Icon
                            icon={ICONS.ARROW_RIGHT}
                            size={'18'}
                            fillRule="evenodd"
                            clipRule="evenodd"
                            stroke={activeRoute.id === id ? COLORS.dark1 : ''}
                            strokeWidth={activeRoute.id === id ? '0.5px' : '0px'}
                        />
                    </ListItemLink>
                </ListItem>
            ))}
            {isShowMyData && (
                <ListItem>
                    <ExternalLink
                        onClick={() => {
                            addRatEvent('mydata', 'mydata_link');
                        }}
                        href={targetUrl}
                        target={'_blank'}
                        tabIndex={0}
                    >
                        <NameIcon>
                            <Icon
                                className={'sidebar__name-icon__left'}
                                icon={ICONS.MY_DATA}
                                size={'24'}
                                viewBoxMax={'24'}
                                fillRule="evenodd"
                                clipRule="evenodd"
                            />
                            {TranslationHelpers.getFormattedMessage('top.page.mydata.title')}
                        </NameIcon>
                        <Icon
                            icon={ICONS.NEW_WINDOW}
                            size={'18'}
                            viewBoxMax={'22'}
                            fillRule="evenodd"
                            clipRule="evenodd"
                            ariaLabel={newTabAriaLabel}
                            tabIndex={0}
                        />
                    </ExternalLink>
                </ListItem>
            )}
            {isShowSignOut && (
                <ListItem>
                    <SignOutLinkContainer qaId="side-menu">
                        <SignOutButton tabIndex={0}>
                            <NameIcon>
                                <Icon
                                    className={'sidebar__name-icon__left'}
                                    icon={ICONS.SIGN_OUT}
                                    size={'24'}
                                    viewBoxMax={'24'}
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                />
                                {TranslationHelpers.getFormattedMessage('app.logout')}
                            </NameIcon>
                        </SignOutButton>
                    </SignOutLinkContainer>
                </ListItem>
            )}
            {returnUrlEnabled && returnUrl && (
                <BackLinkWrapper>
                    <ExternalLink
                        href={returnUrl}
                        onClick={() => {
                            webStorage.removeState();
                        }}
                    >
                        <NameIcon>
                            <Icon
                                className={'sidebar__name-icon__left'}
                                icon={ICONS.BACK}
                                size={'24'}
                                viewBoxMax={'24'}
                                fillRule="evenodd"
                                clipRule="evenodd"
                            />
                            {TranslationHelpers.getFormattedMessage('button.back_to_site', {
                                site: returnUrlText,
                            })}
                        </NameIcon>
                    </ExternalLink>
                </BackLinkWrapper>
            )}
        </List>
    );
};

export default withRouter(injectIntl(SideBar));
